import Cookies from 'js-cookie'
import { jwtDecode } from 'jwt-decode'
import { Action, action, Thunk, thunk } from 'easy-peasy'

interface LoginRedirectModel {
  path: string
  isApiResponse?: boolean
}

export interface User {
  'https://advertiser.bankrate.com/user_id': number
  'https://advertiser.bankrate.com/user_roles': string[]
  'https://advertiser.bankrate.com/using_actions': string
  'https://advertiser.bankrate.com/advertiser_ids': number[]
  'https://advertiser.bankrate.com/auth0_client_id': string
  given_name: string
  family_name: string
  nickname: string
  name: string
  picture: string
  updated_at: string
  email: string
  email_verified: boolean
  iss: string
  aud: string
  iat: number
  exp: number
  sub: string
  roles: string[]
}

export interface AuthModel {
  user: User
  isAuthenticated: boolean
  token: string | null
  roles: string[]

  setUser: Action<AuthModel, User>
  setToken: Action<AuthModel, any>
  setIsAuthenticated: Action<AuthModel, boolean>
  setRoles: Action<AuthModel, any>

  initialize: Thunk<AuthModel>
  getToken: Thunk<AuthModel>
  userHasRole: Thunk<AuthModel, string, boolean>
  isUserAdmin: Thunk<AuthModel>
  loginWithRedirect: Thunk<AuthModel, LoginRedirectModel>
}

const auth: AuthModel = {
  // state
  user: null,
  isAuthenticated: false,
  token: null,
  roles: [],

  // actions
  setUser: action((state, payload) => {
    payload['roles'] = payload['https://advertiser.bankrate.com/user_roles'] // shortcut for getting the roles
    state.user = payload
  }),

  setToken: action((state, payload) => {
    state.token = payload
  }),

  setIsAuthenticated: action((state, payload) => {
    state.isAuthenticated = payload
  }),

  setRoles: action((state, payload) => {
    state.roles = payload
  }),

  // thunks
  getToken: thunk((actions, payload, { getState, getStoreState }) => {
    return Cookies.get('user-auth0-token') || getState().token
  }),

  initialize: thunk(async (actions, _, { getStoreActions }) => {
    // @ts-ignore
    getStoreActions().ui.increaseApiLoading()
    // first check if there is a cookie already
    const authToken = actions.getToken()

    // logged in
    if (authToken) {
      // decode payload
      const decoded = jwtDecode<User>(authToken)

      actions.setUser(decoded)
      actions.setRoles(decoded['https://advertiser.bankrate.com/user_roles'])

      // @ts-ignore
      getStoreActions().ui.decreaseApiLoading()
      actions.setIsAuthenticated(true)
    } else {
      // not logged in
      actions.setIsAuthenticated(false)
      actions.loginWithRedirect({ path: '/' })
    }
  }),

  loginWithRedirect: thunk(
    (actions, payload = { path: '/', isApiResponse: false }) => {
      // Don't do anything if user is already logged in
      if (actions.getToken() && !payload.isApiResponse) {
        return
      }

      const REDIRECT_URL = `${window.location.origin}${payload.path}`
      const BAP_ENV = process.env.APP_ENV === 'prod' ? '' : 'qa.'
      const LOGIN_URL = `https://advertiser.${BAP_ENV}bankrate.com/login`
      window.location.replace(`${LOGIN_URL}?redirectTo=${REDIRECT_URL}`)
    }
  ),

  userHasRole: thunk((actions, role, { getState }) => {
    const { user } = getState()
    if (user) {
      return user['https://advertiser.bankrate.com/user_roles'].includes(role)
    }
    return false
  }),

  isUserAdmin: thunk((actions, payload, { getState }) => {
    const { user } = getState()
    if (user) {
      return user['https://advertiser.bankrate.com/user_roles'].includes(
        'staff'
      )
    }
    return false
  })
}

export default auth
